import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { identityAttributes } from '../core/identity';

/**
 * Put the signed-in org and user on every browser span.
 *
 * The server gets this free: `apps/api`'s proxy injects identity as W3C baggage and
 * `IdentitySpanProcessor` copies it onto every span, so 95% of api request spans are
 * attributable without a route knowing. The browser has no upstream to inject
 * anything, so it stamps its own — and until now it stamped nothing: 0 of 57,066 web
 * records carried `firmable.user_id`, against 73,772 on the api.
 *
 * A processor rather than a hook in `annotateClientSpan`, for two reasons. That
 * function only runs for fetch and XHR spans, and it returns early when it cannot
 * find a URL — so web vitals, document load and anything without one would be
 * missed. And this way the browser mirrors the server's shape, which is the thing
 * that makes one query answer "everything this user touched" across both.
 *
 * Stamped in `onStart`, not `onEnd`: the identity that matters is who was signed in
 * when the work began. A span open across a sign-out would otherwise be attributed
 * to nobody, and one open across a sign-IN to the wrong person.
 */
export class ClientIdentitySpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    try {
      const attributes = identityAttributes();
      if (Object.keys(attributes).length > 0) span.setAttributes(attributes);
    } catch {
      // Attribution must never cost a span.
    }
  }

  onEnd(_span: ReadableSpan): void {}

  async forceFlush(): Promise<void> {}

  async shutdown(): Promise<void> {}
}
